import { useMemo } from 'react';
import { useDynamicFieldsStorage } from './useDynamicFieldsStorage';

// Função para formatar data ISO para formato yyyy-MM-dd
const formatDateForInput = (dateValue: any): string | null => {
  if (!dateValue) return null;
  
  try {
    // Se já é uma string no formato yyyy-MM-dd, retorna como está
    if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      return dateValue;
    }
    
    // Se é uma data ISO, converte para yyyy-MM-dd
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return null;
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  } catch (error) {
    console.error('Erro ao formatar data:', error);
    return null;
  }
};

export const useFormInitialValues = (pageName: string, userData: any) => {
  const { registrationData } = useDynamicFieldsStorage();
  
  const getStorageKey = (pageName: string): keyof typeof registrationData | null => {
    const keyMapping: { [key: string]: keyof typeof registrationData } = {
      'personal': 'personalInfo',
      'contact': 'contactInfo',
      'address': 'addressInfo',
      'additionalInfo': 'additionalInfo'
    };
    
    return keyMapping[pageName] || null;
  };
  
  return useMemo(() => {
    const storageKey = getStorageKey(pageName);
    const savedData = storageKey ? registrationData[storageKey] : null;
    
    console.log(`[useFormInitialValues] ${pageName}:`, {
      storageKey,
      savedData,
      userData,
      registrationData
    });
    
    // Mescla dados salvos com dados do usuário, priorizando dados do usuário
    const mergedData = {
      ...savedData,
      ...userData
    };
    
    // Processa e formata os dados
    const processedData = { ...mergedData };
    
    // Formata campos de data
    if (processedData.birthdate) {
      processedData.birthdate = formatDateForInput(processedData.birthdate);
    }
    
    // Remove valores null/undefined/vazios
    const cleanedData = Object.fromEntries(
      Object.entries(processedData).filter(([_, value]) => value != null && value !== '')
    );
    
    console.log('useFormInitialValues:', {
      pageName,
      savedData,
      userData,
      mergedData: cleanedData
    });
    
    // Log específico para campos de contato
    if (pageName === 'contact') {
      console.log('[useFormInitialValues] Campos de contato:', {
        email: cleanedData.email,
        phoneNumber: cleanedData.phoneNumber,
        hasEmail: !!cleanedData.email,
        hasPhone: !!cleanedData.phoneNumber
      });
    }
    
    // Log específico para campos pessoais
    if (pageName === 'personal') {
      console.log('[useFormInitialValues] Campos pessoais:', {
        fullname: cleanedData.fullname,
        birthdate: cleanedData.birthdate,
        hasFullname: !!cleanedData.fullname,
        hasBirthdate: !!cleanedData.birthdate
      });
    }
    
    return cleanedData;
  }, [pageName, userData, registrationData]);
};
